// Freshness — "is this datum still the present, or is it just the last one we
// happen to have?" (HM-1, 2026-07-15).
//
// Born from the CGM incident: the pipeline died on 10-07 at 20:31, JL kept
// drawing a credible curve for five days, and the only alarm in the whole
// system was Antenore opening the app and looking. The chart asserted that a
// five-day-old reading was "now" because it was the last element of an array.
//
// THE THRESHOLD IS DERIVED FROM THE DATA, NEVER DECLARED. Antenore's verdict
// (2026-07-15): «non puoi sapere se ho un CGM o se metto i dati a manina, non
// ha senso avere soglie così». A constant per metric encodes today's setup —
// glucose is a 5-minute CGM sample this week and a hand-typed reading next
// week (the sensor dies ~25-07 and he's a month without one). The series knows
// its own rhythm; ask it, don't presume. Same statistic feeds the chart's
// gap-splitting, so a broken line and a stale badge can never contradict each
// other: they are one notion — a silence longer than its own habit.
//
// ⛔ "Its own habit" is the SERIES' habit only for a sampled quantity. For an
// event series the habit that counts is the TRAINING calendar's
// (`eventSeriesBreaks.ts`, HM-2) — and then BOTH the break and the dot must
// read that calendar. The invariant is "one notion", not "one array": feeding
// the path one statistic and the dot another is how a continuous line ends in
// a dot that calls itself old.
//
// ⛔ NOT `STALE_DAYS` in coachDashboard.ts. That is coach-attention policy
// ("this athlete hasn't trained in N days" — a judgement about a person, in
// days). This is data-freshness ("this reading no longer describes now" — a
// fact about a sample, in whatever unit the series happens to breathe in).
// Same word, different domains, different thresholds.

export const MINUTE_MS = 60_000;
export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;
export const WEEK_MS = 7 * DAY_MS;

/** Whole days between an instant and now — THE one implementation.
 *
 *  `CN-day-math` counted **five** hand-rolled copies of this three-line sum
 *  (`progressTierTwo.ts` · `Progress/tierTwoMath.ts` · `homeFormBadge.ts` ·
 *  `Home/RecentPrsStrip.tsx` · `coachDashboard.ts`) and recorded the rule for
 *  whoever arrived next: *it gets written here, by the first copy that touches
 *  it.* LG-49 is that copy (2026-09-01). The five stay to be collapsed
 *  boy-scout — they are not this slice's files — but there is now somewhere
 *  for them to go, and no reason for a sixth.
 *
 *  Null in, null out: an instant nobody recorded has no age, and a caller must
 *  be able to tell that from "zero days" ([[feedback_empty_vs_unresolved_collection]]).
 *  Clamped at zero so a clock skew reads "today" rather than a negative age. */
export function daysSince(atMs: number | null, nowMs: number): number | null {
  if (atMs === null) return null;
  return Math.max(0, Math.floor((nowMs - atMs) / DAY_MS));
}

/** Intervals needed before a cadence is a fact rather than a guess. Four gaps
 *  = five points. Under that, `cadenceMs` returns null and every caller must
 *  say "unknown" — the whole point of HM-1 is that silence gets admitted, not
 *  invented. */
const MIN_INTERVALS = 4;

/** How many intervals back to measure. A window, not the whole series: when
 *  the CGM is unplugged, ~30 days of 5-minute samples would hold the median at
 *  5 minutes for a month and every hand-typed reading would read stale from
 *  the moment it landed. Ten intervals let the cadence re-learn the new hand
 *  within days. */
const CADENCE_SAMPLE = 10;

/** How many samples a surface should fetch for `cadenceMs` to have anything to
 *  say — CADENCE_SAMPLE intervals plus the point that opens them. Exported so
 *  the glucose hooks ask for the RIGHT number instead of two hand-kept-in-sync
 *  literals that silently drift from the statistic they feed. */
export const CADENCE_SAMPLE_LIMIT = CADENCE_SAMPLE + 1;

/** A silence this many times the usual interval is an event, not a wobble. */
const CADENCE_MULTIPLE = 3;

/** Floor under the derived threshold. A 5-minute CGM would otherwise turn
 *  stale after 15 minutes, so three readings lost to a tunnel or a phone
 *  asleep would throw a badge that means nothing. The one hand-set number
 *  here, and it only ever makes the app quieter. */
const MIN_STALE_MS = 30 * MINUTE_MS;

/** Median interval between consecutive samples, measured over the most recent
 *  `CADENCE_SAMPLE` gaps. Null when the series is too short to have a rhythm.
 *  `atMsSorted` must be ascending — every chart series is already sorted
 *  chronologically for drawing, so this asks for nothing new. */
export function cadenceMs(atMsSorted: readonly number[]): number | null {
  if (atMsSorted.length < MIN_INTERVALS + 1) return null;
  const deltas: number[] = [];
  const from = Math.max(1, atMsSorted.length - CADENCE_SAMPLE);
  for (let i = from; i < atMsSorted.length; i++) {
    const cur = atMsSorted[i];
    const prev = atMsSorted[i - 1];
    if (cur === undefined || prev === undefined) continue;
    // A non-advancing pair is a duplicate, not a rhythm.
    if (cur - prev > 0) deltas.push(cur - prev);
  }
  if (deltas.length < MIN_INTERVALS) return null;
  deltas.sort((a, b) => a - b);
  const mid = deltas.length >> 1;
  const hi = deltas[mid];
  if (hi === undefined) return null;
  if (deltas.length % 2 === 1) return hi;
  const lo = deltas[mid - 1];
  return lo === undefined ? hi : Math.round((lo + hi) / 2);
}

/** The silence after which this series stops describing the present. Null when
 *  the cadence isn't knowable — callers must then make no claim either way. */
export function staleAfterMs(atMsSorted: readonly number[]): number | null {
  const cadence = cadenceMs(atMsSorted);
  if (cadence === null) return null;
  return Math.max(MIN_STALE_MS, cadence * CADENCE_MULTIPLE);
}

/** Is the gap between two consecutive samples a hole rather than the series
 *  breathing? Used to break the chart path so a hole is DRAWN instead of
 *  interpolated across. Unknown cadence ⇒ false: never invent a hole. */
export function isGap(gapMs: number, cadence: number | null): boolean {
  if (cadence === null) return false;
  return gapMs > Math.max(MIN_STALE_MS, cadence * CADENCE_MULTIPLE);
}

export type Freshness =
  /** The cadence isn't knowable (too few points) — make no claim. */
  | { known: false }
  /** Arriving at its usual rhythm: this datum IS the present. */
  | { known: true; stale: false; ageMs: number }
  /** Silent for longer than its own habit: the last datum is the last one we
   *  have, not the current value. */
  | { known: true; stale: true; ageMs: number };

/** Verdict on a series' most recent sample. `atMsSorted` ascending. */
export function freshness(
  atMsSorted: readonly number[],
  nowMs: number,
): Freshness {
  const last = atMsSorted[atMsSorted.length - 1];
  const threshold = staleAfterMs(atMsSorted);
  if (last === undefined || threshold === null) return { known: false };
  const ageMs = Math.max(0, nowMs - last);
  return { known: true, stale: ageMs > threshold, ageMs };
}
